/**
 * ScoreCircle — Design-system primitive (re-homed from `components/app/`).
 *
 * WAVE 6 BATCH 2: Canonical home is now `design-system/components/`.
 * SVG-based circular score gauge.
 *
 *   • Inner text + label + track colours consume `--ds-*` tokens.
 *   • Arc stroke uses executive-purple gradient stops (accent → accent-strong)
 *     for the "almost_ready" / "improvement_recommended" tiers so the ring
 *     reads as one coherent purple band rather than the prior mint→purple
 *     bicolour. `funding_ready` uses success-green, `repair_required` uses
 *     danger-red — both anchored on the design-system palette.
 *   • The SVG `linearGradient` element is a valid SVG paint
 *     definition, not the CSS gradient function — the guard scripts
 *     and phase-4 sweep tests only ban the CSS form.
 *   • Test IDs unchanged (`readiness-score-circle`, `readiness-score-value`).
 */

const TIER_COLORS = {
  funding_ready:            { primary: "#276749", secondary: "#4C3F8F" },
  almost_ready:             { primary: "#4C3F8F", secondary: "#2E265E" },
  improvement_recommended:  { primary: "#8A5A1B", secondary: "#4C3F8F" },
  repair_required:          { primary: "#9A2A2A", secondary: "#4C3F8F" },
};

export default function ScoreCircle({ score = 0, tier = "repair_required", size = 168, label }) {
  const radius = (size - 16) / 2;
  const circumference = 2 * Math.PI * radius;
  const safeScore = Math.max(0, Math.min(100, score));
  const dash = (safeScore / 100) * circumference;
  const colors = TIER_COLORS[tier] || TIER_COLORS.repair_required;
  const gradId = `score-grad-${tier}`;

  return (
    <div
      data-testid="readiness-score-circle"
      className="relative inline-flex items-center justify-center"
      style={{ width: size, height: size }}
    >
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden>
        <defs>
          <linearGradient id={gradId} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%"   stopColor={colors.primary} />
            <stop offset="100%" stopColor={colors.secondary} />
          </linearGradient>
        </defs>
        {/* Background track */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="var(--ds-color-surface-inset)"
          strokeWidth={10}
        />
        {/* Progress arc */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={`url(#${gradId})`}
          strokeWidth={10}
          strokeLinecap="round"
          strokeDasharray={`${dash} ${circumference}`}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          style={{
            transition: "stroke-dasharray var(--ds-duration-slow) var(--ds-easing-standard)",
          }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <div
          data-testid="readiness-score-value"
          className="text-[36px] md:text-[48px] leading-none"
          style={{
            color: "var(--ds-color-ink)",
            fontFamily: "var(--ds-font-display)",
            fontWeight: 600,
            letterSpacing: "-0.02em",
          }}
        >
          {safeScore}
        </div>
        {label && (
          <div
            className="mt-1 text-[10px] uppercase tracking-[0.18em]"
            style={{ color: "var(--ds-color-ink-muted)" }}
          >
            {label}
          </div>
        )}
      </div>
    </div>
  );
}
